import moment from 'moment';
import { useState } from 'react';
import { useSnackbar } from 'notistack';

import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import LinearProgress from '@mui/material/LinearProgress';
import InputAdornment from '@mui/material/InputAdornment';

import { useGetAiBudgetQuery, useSetAiBudgetMutation } from 'src/services/admin/ai-layer.service';

/** Plain names for the `feature` tags `AiUsageMeter` records. Anything not listed is shown as recorded. */
const FEATURE_LABELS: Record<string, string> = {
  assistant: 'Assistant',
  sourcing_screen: 'Whole database sourcing',
  match: 'Applicant scoring',
  note_extraction: 'Note suggestions',
  digest: 'Daily digest',
  suitable_jobs: 'Suitable jobs',
  outreach: 'Outreach messages',
  gap_analysis: 'Gap analysis',
  job_brief: 'Job briefs',
  inbound_parse: 'Inbound applications',
  chat: 'Old chat (Linki)',
  chat_intent: 'Old chat (Linki)',
};

const featureLabel = (feature: string) => FEATURE_LABELS[feature] ?? feature.replace(/_/g, ' ');

/** Spend is in US dollars, the currency the AI provider bills in. Small amounts keep their cents. */
export const usd = (amount: number) =>
  `US$${amount.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: amount < 1 ? 4 : 2 })}`;

const budgetText = (amount: number | null) => (amount === null ? 'no budget' : usd(amount));

const BAR_COLOR = { none: 'primary', ok: 'primary', warning: 'warning', over: 'error' } as const;

/**
 * This month's AI spend and the monthly budget, inside the System Dashboard AI layer card
 * (JT1-1822). JobTetris admins only, like the card. Past 80% it warns; past 100% the assistant
 * moves to the cheaper model (D20). Sourcing and automatic scoring are never limited.
 */
export default function AiBudgetSection({ organizationId }: { organizationId: string }) {
  const { enqueueSnackbar } = useSnackbar();
  const { currentData: budget, isLoading } = useGetAiBudgetQuery(organizationId, { skip: !organizationId });
  const [setAiBudget, { isLoading: saving }] = useSetAiBudgetMutation();
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState('');

  if (isLoading) return <Skeleton variant="rounded" height={80} />;
  if (!budget) return null;

  const month = moment(budget.month_start).format('MMMM YYYY');
  const parsed = amount.trim() === '' ? NaN : Number(amount);
  const valid = Number.isFinite(parsed) && parsed >= 0 && parsed <= 1_000_000;

  const save = async (monthlyBudgetUsd: number | null) => {
    try {
      await setAiBudget({ organizationId, monthlyBudgetUsd }).unwrap();
      enqueueSnackbar(monthlyBudgetUsd === null ? 'Budget removed.' : 'Budget saved.', { variant: 'success' });
      setEditing(false);
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message ?? 'That did not work. Please try again.', { variant: 'error' });
    }
  };

  const openEditor = () => {
    setAmount(budget.budget_usd === null ? '' : String(budget.budget_usd));
    setEditing(true);
  };

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography variant="subtitle2">AI spend, {month}</Typography>
        <Button size="small" onClick={openEditor}>{budget.budget_usd === null ? 'Set budget' : 'Change budget'}</Button>
      </Stack>

      <Stack spacing={0.75}>
        <Typography variant="body2">
          {usd(budget.spent_usd)} spent of {budget.budget_usd === null ? 'no budget set' : `${usd(budget.budget_usd)} a month`}
          {budget.percent_used !== null && ` (${budget.percent_used}%)`}
        </Typography>
        {budget.percent_used !== null && (
          <LinearProgress
            variant="determinate"
            value={Math.min(budget.percent_used, 100)}
            color={BAR_COLOR[budget.state]}
            sx={{ height: 6, borderRadius: 1 }}
          />
        )}
      </Stack>

      {budget.state === 'warning' && (
        <Alert severity="warning">
          Over 80% of this month&apos;s budget. At 100% the assistant moves to the cheaper model. The agency&apos;s admins see this
          warning too, as a percentage.
        </Alert>
      )}
      {budget.state === 'over' && (
        <Alert severity="error">
          Budget used up. The assistant is on the cheaper model until the month resets. Sourcing and automatic scoring carry on
          as normal.
        </Alert>
      )}

      {budget.by_feature.length > 0 ? (
        <Stack spacing={0.5}>
          {budget.by_feature.map((row) => (
            <Stack key={row.feature} direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">{featureLabel(row.feature)}</Typography>
              <Typography variant="body2">
                {usd(row.spent_usd)} <Typography component="span" variant="caption" color="text.disabled">({row.calls.toLocaleString('en-AU')} {row.calls === 1 ? 'call' : 'calls'})</Typography>
              </Typography>
            </Stack>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">No AI use recorded this month.</Typography>
      )}

      {budget.changes.length > 0 && (
        <Stack spacing={0.5}>
          {budget.changes.map((change, i) => (
            <Typography key={i} variant="caption" color="text.secondary">
              Budget changed from {budgetText(change.before)} to {budgetText(change.after)} by {change.by ?? 'unknown'},{' '}
              {moment(change.at).format('D MMM YYYY, h:mm a')}
            </Typography>
          ))}
        </Stack>
      )}

      <Dialog open={editing} onClose={() => setEditing(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Monthly AI budget</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              autoFocus
              label="Budget per month"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ min: 0, step: 1 }}
              InputProps={{ startAdornment: <InputAdornment position="start">US$</InputAdornment> }}
              error={amount !== '' && !valid}
              helperText="In US dollars, the currency the AI provider bills in. The month runs on Sydney time."
            />
            <Typography variant="body2" color="text.secondary">
              At 80% the agency&apos;s admins are warned. At 100% the assistant moves to a cheaper model. Nothing is ever switched
              off.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          {budget.budget_usd !== null && (
            <Button color="error" disabled={saving} onClick={() => save(null)} sx={{ mr: 'auto' }}>Remove budget</Button>
          )}
          <Button onClick={() => setEditing(false)}>Cancel</Button>
          <LoadingButton variant="contained" loading={saving} disabled={!valid} onClick={() => save(Math.round(parsed * 100) / 100)}>
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
